import React, { useContext } from 'react';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import { Order } from '../../../types/order';
import { ProductTransactionContext } from '../../../context/ProductTransactionContext';
import Provider from '../../../providers/common/Provider';
import { PdfTemplate, PdfTemplateEntitySubtype, PdfTemplateInput } from '../../../types/pdf_template';
import PdfTemplatePropertiesEditor from './PdfTemplatePropertiesEditor';
import { PdfTableType } from './PdfComponent';
import { getOrderDataKeys, getProductTransactionDataKeys, PdfSubkey } from './data/PdfDataKey';

interface PdfTemplatePropertiesEditorProps {
  template: PdfTemplate;
  onDone: (template: PdfTemplateInput) => void;
}

export default function PdfTemplateEditor({ template, onDone }: PdfTemplatePropertiesEditorProps) {
  return (
    <Provider order container bin lot productTransaction customField stockLocation productMasterData>
      <Element template={template} onDone={onDone} />
    </Provider>
  );
}

function Element({ template, onDone }: PdfTemplatePropertiesEditorProps) {
  const { customFields } = useContext(CustomFieldContext);
  const { productTransactions } = useContext(ProductTransactionContext);

  const validSubkeys = new Set<PdfSubkey>();

  if (template.entitySubtype === PdfTemplateEntitySubtype.inbound) validSubkeys.add(PdfSubkey.destination);
  else if (template.entitySubtype === PdfTemplateEntitySubtype.outbound) validSubkeys.add(PdfSubkey.source);
  else if (template.entitySubtype === PdfTemplateEntitySubtype.move) {
    validSubkeys.add(PdfSubkey.destination);
    validSubkeys.add(PdfSubkey.source);
  }

  const datakeys = getOrderDataKeys(customFields).filter(dk => !dk.subkey || validSubkeys.has(dk.subkey));
  const imageDataKeys = getOrderDataKeys(customFields, true).filter(dk => !dk.subkey || validSubkeys.has(dk.subkey));

  return (
    <PdfTemplatePropertiesEditor<Order>
      template={template}
      onDone={onDone}
      dataKeys={datakeys}
      imageDataKeys={imageDataKeys}
      tableProviders={[
        {
          tableType: PdfTableType.productTransaction,
          columns: getProductTransactionDataKeys(customFields, [...validSubkeys]).filter(
            dk => !dk.subkey || validSubkeys.has(dk.subkey),
          ),
          rowResolver: (order?: Order) => {
            if (!order) return [];
            return [...productTransactions.values()].filter(pt => pt.parentId === order.id);
          },
        },
      ]}
    />
  );
}
